import React, { useCallback, useContext, useEffect, useState } from "react";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import VendorContext from "../../../../contexts/VendorContext";
import ISerialWorkflow from "../../../../models/ISerialWorkflow";
import ISerialWorkflowStep from "../../../../models/ISerialWorkflowStep";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import {
    getSerialWorkflows,
    createSerialWorkflow,
    updateSerialWorkflow,
    createSerialWorkflowStep,
    updateSerialWorkflowStep,
    deleteSerialWorkflowStep,
    reorderSerialWorkflowSteps,
} from "../../../../services/SerialWorkflowService";
import { ProductionAuthContext } from "../../../../services/ProductionService";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import SerialWorkflowStepRow from "./SerialWorkflowStepRow";
import SerialWorkflowStepModal from "./SerialWorkflowStepModal";
import OrderStateBridgePicker from "./OrderStateBridgePicker";
import "./SerialWorkflowSettings.scss";

type Props = {
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    setActivityIndicator: (activityOn: boolean) => void;
};

const SerialWorkflowSettings: React.FC<Props> = ({ notifyFn, setActivityIndicator }) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const ctx: ProductionAuthContext = { userModel, supplierModel, vendorModel };

    const [workflow, setWorkflow] = useState<ISerialWorkflow | null>(null);
    const [steps, setSteps] = useState<ISerialWorkflowStep[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showStepModal, setShowStepModal] = useState(false);
    const [editingStep, setEditingStep] = useState<ISerialWorkflowStep | null>(null);

    const activeSteps = steps.filter((s) => s.is_active !== false);

    const fetchWorkflows = useCallback(async () => {
        setIsLoading(true);
        try {
            const workflows = await getSerialWorkflows(ctx);
            if (workflows.length > 0) {
                const wf = workflows[0];
                setWorkflow(wf);
                setSteps((wf.Serial_Workflow_Steps || []).sort((a, b) => a.sequence_order - b.sequence_order));
            }
        } catch (err) {
            console.error("Failed to fetch serial workflows", err);
        } finally {
            setIsLoading(false);
        }
    }, [userModel.guid, supplierModel.guid]);

    useEffect(() => {
        fetchWorkflows();
    }, [fetchWorkflows]);

    const handleOrderStateChange = async (orderStateId: number | null) => {
        if (!workflow) return;
        try {
            setActivityIndicator(true);
            const updated = await updateSerialWorkflow(ctx, workflow.id, {
                on_completion_order_state_id: orderStateId,
            });
            setWorkflow({ ...workflow, on_completion_order_state_id: updated.on_completion_order_state_id });
        } catch (err) {
            console.error("Failed to update workflow", err);
            notifyFn({ message: "Failed to update workflow" } as IErrorDTO);
        } finally {
            setActivityIndicator(false);
        }
    };

    const handleAddStep = () => {
        setEditingStep(null);
        setShowStepModal(true);
    };

    const handleEditStep = (step: ISerialWorkflowStep) => {
        setEditingStep(step);
        setShowStepModal(true);
    };

    const handleDeactivateStep = async (step: ISerialWorkflowStep) => {
        if (!workflow) return;
        try {
            setActivityIndicator(true);
            await deleteSerialWorkflowStep(ctx, step.id);
            setSteps((prev) => prev.map((s) => (s.id === step.id ? { ...s, is_active: false } : s)));
        } catch (err) {
            console.error("Failed to deactivate step", err);
            notifyFn({ message: "Failed to deactivate step. Serials may currently be at this step." } as IErrorDTO);
        } finally {
            setActivityIndicator(false);
        }
    };

    const handleSaveStep = async (data: { name: string; is_production_start: boolean; is_terminal: boolean }) => {
        if (!workflow) return;
        try {
            setActivityIndicator(true);

            if (editingStep) {
                const updated = await updateSerialWorkflowStep(ctx, editingStep.id, data);
                setSteps((prev) => prev.map((s) => (s.id === editingStep.id ? { ...s, ...updated } : s)));
            } else {
                const maxSeq = activeSteps.length > 0 ? Math.max(...activeSteps.map((s) => s.sequence_order)) : 0;
                const created = await createSerialWorkflowStep(ctx, workflow.id, {
                    ...data,
                    name: data.name,
                    sequence_order: maxSeq + 1,
                    is_active: true,
                });
                setSteps((prev) => [...prev, created].sort((a, b) => a.sequence_order - b.sequence_order));
            }

            setShowStepModal(false);
            setEditingStep(null);
        } catch (err) {
            console.error("Failed to save step", err);
            notifyFn({ message: "Failed to save step" } as IErrorDTO);
        } finally {
            setActivityIndicator(false);
        }
    };

    const onDragEnd = async (result: DropResult) => {
        if (!result.destination || !workflow) return;
        if (result.source.index === result.destination.index) return;

        const reordered = Array.from(activeSteps);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);

        const updatedSteps = reordered.map((s, i) => ({ ...s, sequence_order: i + 1 }));
        const inactiveSteps = steps.filter((s) => s.is_active === false);
        setSteps([...updatedSteps, ...inactiveSteps].sort((a, b) => a.sequence_order - b.sequence_order));

        try {
            await reorderSerialWorkflowSteps(
                ctx,
                workflow.id,
                updatedSteps.map((s) => s.id)
            );
        } catch (err) {
            console.error("Failed to reorder steps", err);
            notifyFn({ message: "Failed to reorder steps" } as IErrorDTO);
            await fetchWorkflows();
        }
    };

    if (isLoading) {
        return (
            <div className="SerialWorkflowSettings p-3">
                <i className="fas fa-spinner fa-spin me-2" />
                Loading...
            </div>
        );
    }

    const handleCreateWorkflow = async () => {
        try {
            setActivityIndicator(true);
            const created = await createSerialWorkflow(ctx, {
                name: "Production Workflow",
                is_default: true,
                is_active: true,
            });
            setWorkflow(created);
            setSteps((created.Serial_Workflow_Steps || []).sort((a, b) => a.sequence_order - b.sequence_order));
        } catch (err) {
            console.error("Failed to create workflow", err);
            notifyFn({ message: "Failed to create production workflow" } as IErrorDTO);
        } finally {
            setActivityIndicator(false);
        }
    };

    if (!workflow) {
        return (
            <div className="SerialWorkflowSettings p-3">
                <p className="text-muted mb-2">
                    <i className="fad fa-info-circle me-1" />
                    No production workflow configured yet. Create one to define the stages your serials move through during manufacturing.
                </p>
                <OwButton
                    title={"create_production_workflow"}
                    className={"btn-primary"}
                    leftIconClass={"fas fa-plus"}
                    fn={handleCreateWorkflow}
                />
            </div>
        );
    }

    return (
        <div className="SerialWorkflowSettings p-3">
            <p className="text-muted small mb-3">
                <i className="fad fa-info-circle me-1" />
                These are the stages your serials move through during manufacturing &mdash; separate from the order&apos;s fulfillment
                workflow.
            </p>

            <OrderStateBridgePicker
                selectedOrderStateId={workflow.on_completion_order_state_id}
                orderProgressStates={supplierModel.order_progress_states || []}
                onChange={handleOrderStateChange}
            />

            <OwLabel title={"production_workflow"} leftIconClass={"fad fa-industry"} className={"mt-3 mb-2"} />

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="serial-workflow-steps" direction="vertical">
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`steps-droppable ${snapshot.isDraggingOver ? "dragging-over" : ""}`}>
                            {activeSteps.map((step, index) => (
                                <SerialWorkflowStepRow
                                    key={step.id}
                                    step={step}
                                    index={index}
                                    onEdit={handleEditStep}
                                    onDeactivate={handleDeactivateStep}
                                />
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {activeSteps.length === 0 && (
                <p className="text-muted text-center py-3">No steps configured. Add your first production step.</p>
            )}

            <div className="mt-2">
                <OwButton
                    title={"add_production_step"}
                    className={"btn-outline-primary btn-sm"}
                    leftIconClass={"fas fa-plus"}
                    fn={handleAddStep}
                />
            </div>

            {showStepModal && (
                <SerialWorkflowStepModal
                    isOpen={showStepModal}
                    step={editingStep}
                    existingSteps={activeSteps}
                    onClose={() => {
                        setShowStepModal(false);
                        setEditingStep(null);
                    }}
                    onSave={handleSaveStep}
                />
            )}
        </div>
    );
};

export default SerialWorkflowSettings;
